import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {concatMap, filter, from, interval, ReplaySubject, Subject, Subscription, take, throttleTime} from 'rxjs';
import {GroupId, StepId, UUID} from '../../models/uuid';
import {
  Convo,
  ConvoError,
  ConvoStatus,
  ConvoStatusMessage,
  ConvoUpdateMessage,
  Step,
  StepConfig,
  StepDeletionMessage,
  StepRegenMessage,
  StepUpdateMessage,
} from '../../models/convo';
import {MotiGrantee} from '../../models/moti-fga/moti-grantee';
import {TopicConfigConvos} from '../../models/topic';
import {ConvoBubbleGroupComponent} from './convo-bubble-group/convo-bubble-group.component';
import {InfoCircleComponent} from '../info-circle/info-circle.component';
import {SpinnerComponent} from 'src/lib/components/spinner/spinner.component';
import {UserAvatarComponent} from '../../../shared/components/user-avatar/user-avatar.component';
import {FormsModule} from '@angular/forms';
import {StepCollection} from 'src/moti/models/step-collection';
import {User, UserId} from '../../../shared/models/user';
import {ConvoService, MAX_STEPS_PER_GROUP} from 'src/moti/services/convo.service';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {ShadeService} from 'src/moti/services/shade.service';
import {SessionService} from '../../../core/services/session.service';
import {UsersService} from '../../../core/services/users.service';
import {
  FuncConvoNewStep,
  FuncConvoStatus,
  FuncConvoStepDeletion,
  FuncConvoStepRegen,
  FuncConvoStepUpdate,
  FuncConvoUpdate,
  FuncError,
} from 'src/moti/models/wss/message';
import {Alias, CurrentModels, getAliasName, getAliasProvider, isDeepModel, Provider} from 'src/moti/models/llm/llm';
import {MotiTimestamp} from 'src/moti/models/time';
import {MotiOption, MotiOptionGroup} from 'src/moti/models/moti-option';
import {IconType} from 'src/moti/models/moti-icon';
import {MotiUrlService} from 'src/moti/services/moti-url.service';
import {ContentEditableDirective} from '../../directives/content-editable.directive';
import {EmptySectionPlaceholderComponent} from '../empty-section-placeholder/empty-section-placeholder.component';
import {FeatureFlagsService, FeatureFlag} from 'src/core/services/feature-flags.service';

const MAX_WORDS_PER_STEP = 2000;

@Component({
  selector: 'moti-convo',
  templateUrl: './convo.component.html',
  styleUrls: ['./convo.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ConvoBubbleGroupComponent,
    InfoCircleComponent,
    SpinnerComponent,
    UserAvatarComponent,
    FormsModule,
    RouterLink,
    ContentEditableDirective,
    EmptySectionPlaceholderComponent,
  ],
})
export class ConvoComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() convo: Convo;
  @Input() topicConfig: TopicConfigConvos; // for default model setting only
  @Input() initialStep: Step;

  /**
   * Only needed if the user can edit data sources for the convo
   */
  @Input() owner: MotiGrantee;

  /**
   * Careful distinction here between false and undefined. The convo text input will
   * show a loader until this is set to a boolean
   */
  @Input() ended: boolean | undefined;

  @Input() canChat: boolean;
  @Input() canAddDataSource: boolean;
  @Input() canViewDataSources: boolean;
  @Input() canViewUsers: boolean;

  /**
   * This would be invoked if the server sends a convo-update message via WebSocket
   */
  @Output() convoUpdated$: EventEmitter<Convo> = new EventEmitter<Convo>();

  /**
   * Emitted when the convo is fully loaded and ready to display
   */
  @Output() ready = new EventEmitter<void>();

  status: ConvoStatus;

  /**
   * All steps, even those not part of a group, will get stored in here. This is to
   * avoid having to constantly check types. Since StepGroup.config.primary is itself
   * a Step object, this should work pretty cleanly for non-group steps too.
   */
  stepCollections: StepCollection[] = [];
  loadedOnce: boolean = false;
  showModelMenu: boolean = false;
  modelOrder: Alias[] = [];
  enableAutoScroll: boolean = true;
  waitingForResponse: boolean = true;
  retrying: boolean = false;

  private convoSub: Subscription;
  private messagesSub: Subscription;
  private retryingSub: Subscription;
  private pullStepsSub: Subscription;
  private usageSub: Subscription;

  user: User;
  userEntry: string = '';
  textInputBufferHeight: number;
  queryParamStep: UUID;

  userIds: UserId[] = [];
  contributorIds: UserId[] = []; // users who have actually sent a message in this convo
  contributorUsers: Map<UserId, User> = new Map(); // Map of user ID to User object for contributors

  /**
   * Whether to set the same-named property in the payload to the API, which determines
   * whether Moti will respond to the newly sent message
   */
  shouldDeferResponse: boolean;
  /**
   * Whether adding "@moti" matters presently; consequently, also whether to show
   * the info box explaining how to use "@moti"
   */
  shouldShowDeferResponseState: boolean;

  @ViewChild('convoBubblesArea', {static: true}) private $convoBubblesArea: ElementRef;
  @ViewChild('scrollBottom', {static: true}) private $scrollBottom: ElementRef;
  @ViewChild('textInputArea', {static: true}) private $textInputArea: ElementRef;
  @ViewChild('textInput') private $textInput: ElementRef;
  @ViewChild('modelSelectionOverlay') private $modelSelectionOverlay: ElementRef;

  /**
   * Logs what the newest step timestamp was the last time the user triggered a scroll
   * event.
   *
   * @private
   */
  private newestStepTimestampAtLastScroll: string = '';
  private newestStepTimestamp: string = '';
  private newestStepId: StepId;

  showScrollToBottomButton: boolean = false;


  constructor(
    private convoSvc: ConvoService,
    private route: ActivatedRoute,
    private shade: ShadeService,
    private sess: SessionService,
    private featureFlagsService: FeatureFlagsService,
    private usersService: UsersService,
  ) {
    for (let i = 0; i < MAX_STEPS_PER_GROUP; i++) {
      this.modelMenuOptionGroups.push(this.generateModelMenuOptionGroup(i));
    }
  }


  ngAfterViewInit() {
    this.startScrollListener();
  }


  private startScrollListener() {
    this.$convoBubblesArea.nativeElement.addEventListener('wheel', this._listenerForScrolling.bind(this));
    this.$convoBubblesArea.nativeElement.addEventListener('touchmove', this._listenerForScrolling.bind(this));
    this.$convoBubblesArea.nativeElement.addEventListener('click', this._listenerForScrolling.bind(this));
    this.$convoBubblesArea.nativeElement.addEventListener('scroll', this._listenerForScrolling.bind(this));
  }


  private stopScrollListener() {
    this.$convoBubblesArea.nativeElement.removeEventListener('wheel', this._listenerForScrolling.bind(this));
    this.$convoBubblesArea.nativeElement.removeEventListener('touchmove', this._listenerForScrolling.bind(this));
    this.$convoBubblesArea.nativeElement.removeEventListener('click', this._listenerForScrolling.bind(this));
    this.$convoBubblesArea.nativeElement.removeEventListener('scroll', this._listenerForScrolling.bind(this));
  }


  private _listenerForScrolling() {
    const atBottom = this.isUserAtBottom();

    if (atBottom) {
      this.newestStepTimestampAtLastScroll = this.newestStepTimestamp;
    }

    this.showScrollToBottomButton = !atBottom;
    this.enableAutoScroll = atBottom;
  }


  private isUserAtBottom(): boolean {
    const element = this.$convoBubblesArea.nativeElement;
    const scrollThreshold = 75;
    return element.scrollHeight - element.scrollTop - element.clientHeight < scrollThreshold;
  }


  protected get newMessagesCount(): number {
    if (!this.newestStepTimestampAtLastScroll || !this.showScrollToBottomButton) {
      return 0;
    }

    return this.stepCollections.filter(collection => {
      const steps = Object.values(collection.stepMap);
      const firstStep = steps[0];
      return firstStep &&
        firstStep.createdBy !== this.sess.user.ID &&
        firstStep.createdAt > this.newestStepTimestampAtLastScroll;
    }).length;
  }


  /**
   * Answers the question: has the user scrolled since the newest step shown on the screen was first
   * rendered, even partially?
   *
   * @private
   */
  private get userHasScrolled(): boolean {
    return this.newestStepTimestampAtLastScroll >= this.newestStepTimestamp;
  }


  /**
   * This can be called many times in a short duration and the resulting scrolling will
   * be throttled.
   *
   * @private
   */
  private autoScrollToStepThrottled() {
    this._autoScrollToStepThrottler$.next(null);
  }


  /**
   * Utilize this by calling autoScrollToStepThrottled
   *
   * @private
   * @see autoScrollToStepThrottled
   */
  private _autoScrollToStepThrottler$ = new Subject();


  private scrollToBottom(behavior: ScrollBehavior) {
    this.$convoBubblesArea.nativeElement.scrollTo({
      top: this.$scrollBottom.nativeElement.offsetTop,
      behavior: behavior,
    });
  }


  private textRenderThrottler$ = new Subject<{ step: Step, char: string }>();


  ngOnInit() {
    this.setUpThrottledFuncs();
    this.user = this.sess.user;

    if (this.initialStep) {
      this.spliceStep(this.initialStep);
    }

    this.route.queryParams.subscribe({
      next: (params) => {
        if (params['step']) {
          this.queryParamStep = params['step'];

          if (this.queryParamStep) {
            this.scrollToStep(this.queryParamStep);
          }
        }
      },
    });

    this.loadAllAuthorizedUsers();
    this.setUpWebSocket();
  }


  /**
   * Loads all users who have permission to access this convo directly or by virtue
   * of team or topic membership.
   *
   * Contributors are not identified in this function; that happens in spliceStep.
   *
   * @private
   */
  private loadAllAuthorizedUsers() {
    if (this.canViewUsers) {
      const userIds = new Set<UserId>();
      this.convoSvc.getAllUserIds$(this.convo.id).subscribe({
        next: ids => {
          for (let id of ids) {
            userIds.add(id);
          }

          this.userIds = Array.from(userIds);
        },
      });
    }
  }


  private setUpThrottledFuncs() {
    this.textRenderThrottler$.pipe(
      concatMap(value =>
        interval(1).pipe(
          take(1),
          concatMap(() => from([value])),
        ),
      ),
    ).subscribe({
      next: next => {
        next.step.text += next.char;
      },
    });

    this._autoScrollToStepThrottler$.pipe(
      throttleTime(200, null, {
        leading: true,
        trailing: true,
      }),
    ).subscribe({
        next: () => {
          if (!this.userHasScrolled && this.enableAutoScroll) {
            if (this.newestStepId) {
              this.scrollToStep(this.newestStepId);
            } else {
              this.scrollToBottom('smooth');
            }
          }
        },
      },
    );
  }


  private setUpWebSocket() {
    this.fetchSteps();

    this.convoSub = this.convoSvc.startConvo$(this.convo.id).subscribe({
      next: (messages$) => {
        this.waitingForResponse = false;

        // const throttleConditionFn = (msg: Message): boolean => {
        //   return msg._ws.funcName === FuncConvoNewStep;
        // };
        //
        // const [toThrottle$, notToThrottle$] = partition(messages$, throttleConditionFn);
        //
        // const throttled$ = toThrottle$.pipe(
        //   throttleTime(100, null, {
        //     leading: true,
        //     trailing: true,
        //   }),
        // );
        //
        // const merged$ = merge(
        //   throttled$,
        //   notToThrottle$,
        // );

        this.messagesSub = messages$.subscribe({
          next: (msg) => {
            switch (msg._ws.funcName) {
              case FuncError: {
                const err = msg.payload as ConvoError;

                if (err.error === 'unauthorized') {
                  this.convoSub.unsubscribe();
                } else {
                  if (err.stepId) {
                    const collection = this.stepCollections.find(c => c.stepMap[err.stepId]);

                    if (collection) {
                      const step = collection.stepMap[err.stepId];
                      step.errorAt = new Date().toUTCString();
                      step.partialOrder = undefined;

                      collection.steps$.next(step);
                    }
                  }
                }

                break;
              }
              case FuncConvoNewStep: {
                const step = msg.payload as Step;
                const wasAtBottom = this.isUserAtBottom();

                this.spliceStep(step);

                if (step.createdBy === this.sess.user.ID) {
                  this.enableAutoScroll = true;
                  this.newestStepTimestampAtLastScroll = '';
                  this.autoScrollToStepThrottled();
                } else {
                  if (wasAtBottom) {
                    this.enableAutoScroll = true;
                  }
                  this.autoScrollToStepThrottled();
                }

                break;
              }
              case FuncConvoStatus: {
                const status = (msg as ConvoStatusMessage).payload;

                if (!this.status || status.timestamp > this.status.timestamp) {
                  this.status = status;
                  this.waitingForResponse = this.status.waitingForProvider;
                }

                break;
              }
              case FuncConvoUpdate: {
                const update = (msg as ConvoUpdateMessage).payload;

                this.convoUpdated$.emit(update.convo);

                break;
              }
              case FuncConvoStepDeletion: {
                const deletion = (msg as StepDeletionMessage).payload;

                this.unspliceStep(deletion.stepId);

                break;
              }
              case FuncConvoStepRegen: {
                const regen = (msg as StepRegenMessage).payload;

                if (!regen.newStep) {
                  break;
                }

                const newStepExists = this.stepCollections.some(c => c.stepMap[regen.newStep.id]);

                if (!newStepExists) {
                  const oldCollectionIndex = this.stepCollections.findIndex(c => c.stepMap[regen.stepId]);
                  this.unspliceStep(regen.stepId);
                  this.spliceStep(regen.newStep, oldCollectionIndex !== -1 ? oldCollectionIndex : undefined);
                } else {
                  this.spliceStep(regen.newStep);
                }

                if (this.isUserAtBottom()) {
                  this.enableAutoScroll = true;
                }

                this.autoScrollToStepThrottled();
                break;
              }
              case FuncConvoStepUpdate: {
                const update = (msg as StepUpdateMessage).payload;
                const collectionIndex = this.stepCollections.findIndex(c => c.stepMap[update.stepId]);
                const collection = this.stepCollections[collectionIndex];

                if (collection) {
                  if (update.isAlternate === false) {
                    collection.stepPrimaryChanges$.next(update.stepId);
                  }

                  if (update.groupId) {
                    const step = collection.stepMap[update.stepId];

                    if (!step.groupId) {
                      // If we got here, we're dealing with a step that was originally not part of a
                      // group and is now part of one. So we remove the collection that was originally
                      // just this step and reprocess the step through spliceStep after setting the
                      // step's new group ID
                      this.stepCollections.splice(collectionIndex, 1);

                      step.groupId = update.groupId;
                      this.spliceStep(step);
                    }
                  }
                }

                break;
              }
              default: {
                console.error(msg);
              }
            }
          },
        });

        this.retryingSub = this.convoSvc.retryingConvoIds$.subscribe({
          next: (convoIds) => {
            const retrying = convoIds.has(this.convo.id);

            if (!retrying && this.retrying && this.stepCollections?.length) {
              // Pull latest steps if there was a connection blip
              this.fetchSteps(this.newestStepTimestamp);
            }

            this.retrying = retrying;
          },
        });
      },
      error: () => {
        this.shade.errorDialog();
      },
    });

    this.pullStepsSub = this.convoSvc.triggerFetchSteps$.pipe(
      filter(u => u === this.convo.id),
    ).subscribe({
      next: () => {
        this.fetchSteps(this.newestStepTimestamp);
      },
    });
  }


  ngOnDestroy() {
    this.stopScrollListener();
    this._autoScrollToStepThrottler$.complete();
    this.textRenderThrottler$.complete();

    this.convoSub?.unsubscribe();
    this.messagesSub?.unsubscribe();
    this.retryingSub?.unsubscribe();
    this.pullStepsSub?.unsubscribe();
    this.usageSub?.unsubscribe();
  }


  ngOnChanges(changes: SimpleChanges) {
    /**
     * @see ended
     */
    if (changes['ended']) {
      this.focusOnInput();
    }
  }


  /**
   * Inserts a step object into this.steps at the right index (using splice) in a way that avoids
   * creating new objects that are equivalent to ones that already exist.
   *
   * @param step
   */
  private spliceStep(step: Step, atIndex?: number) {
    if (step.createdAt > this.newestStepTimestamp) {
      this.newestStepTimestamp = step.createdAt;
      this.newestStepId = step.id;
    }

    if (!step.createdByProvider) {
      // Ensure we're keeping track of all the users who have contributed at least
      // one message in this convo
      if (!this.contributorIds.includes(step.createdBy)) {
        this.contributorIds.push(step.createdBy);
        this.fetchContributorUser(step.createdBy);
        this.setDeferralProperties();
      }
    }

    let collectionId: GroupId | StepId;

    if (step.groupId && step.createdByProvider) {
      collectionId = step.groupId;
    } else {
      collectionId = step.id;
    }

    let collection = this.stepCollections.find(c => {
      return c.id === collectionId;
    });

    if (collection) {
      collection.steps$.next(step);
      collection.stepMap[step.id] = step;
    } else {
      collection = {
        id: collectionId,
        instanceId: crypto.randomUUID(),
        steps$: new ReplaySubject<Step>(),
        stepDeletions$: new ReplaySubject<StepId>(),
        stepPrimaryChanges$: new ReplaySubject<StepId>(),
        stepMap: {},
        timestamp: step.createdAt,
      };

      collection.steps$.next(step);
      collection.stepMap[step.id] = step;

      if (atIndex === undefined) {
        atIndex = 0;
        for (let x = this.stepCollections.length - 1; x >= 0; x--) {
          const oldResult = this.stepCollections[x];

          if (new Date(step.createdAt) > new Date(oldResult.timestamp)) {
            atIndex = x + 1;
            break;
          }
        }
      }

      this.stepCollections.splice(atIndex, 0, collection);
    }

    this.resizeTextInputBuffer();
  }


  private unspliceStep(stepId: StepId) {
    const collectionIndex = this.stepCollections.findIndex(c => c.stepMap[stepId]);

    if (collectionIndex == -1) {
      return;
    }

    const collection = this.stepCollections[collectionIndex];

    if (Object.values(collection.stepMap).length > 1) {
      delete collection.stepMap[stepId];
      collection?.stepDeletions$.next(stepId);
    } else {
      this.stepCollections.splice(collectionIndex, 1);
    }
  }


  /**
   * For both getting steps at component initialization and fetching steps after a Websocket connection
   * issue to ensure no steps were missed during the connection problem.
   *
   * @param after
   * @private
   */
  private fetchSteps(after?: MotiTimestamp) {
    this.convoSvc.getSteps$(this.convo.id, after).subscribe({
      next: steps => {
        if (this.ended === undefined) {
          this.ended = false;
        }

        if (after) {
          for (let step of steps) {
            this.spliceStep(step);
          }

          return;
        }

        setTimeout(() => {
          // If we fetched all steps, reset the collections array
          this.stepCollections = [];

          for (let step of steps) {
            this.spliceStep(step);
          }

          this.resizeTextInputBuffer();
          this.scrollToBottom('auto');

          if (!this.loadedOnce) {
            setTimeout(() => {
              this.loadedOnce = true;
              this.updateModelOrderFromSteps();
              this.focusOnInput();
              this.ready.emit();

              setTimeout(() => {
                if (this.queryParamStep) {
                  this.scrollToStep(this.queryParamStep);
                } else {
                  this.scrollToBottom('smooth');
                }
              });
            });
          }
        });
      },
    });
  }


  /**
   * Whenever the order of the models is determined as a result of some HTTP/WS
   * operation, call this to ensure:
   *  1. multi-LLM steps show models in the correct order
   *  2. the model menu is current
   */
  private updateModelOrderFromSteps() {
    // Reset each menu to its very first available option
    this.modelMenuOptionGroups.map(og => og.changeSelected(og.options[0].id, true));

    // Find StepData objects that are from the provider and which aren't manual,
    // since such objects are the only ones that can tell us what the last step
    // group's set of models was
    const providerStepData = this.stepCollections.filter(c => {
      const pStep = Object.values(c.stepMap).find(s => !s.isAlternate);

      if (!pStep) {
        // We need a group with a primary step, so return false if we didn't
        // find one
        return false;
      }

      // We need a group generated by a provider, so return whether the
      // primary step we found reflects such a group
      return pStep.createdByProvider && pStep.config.provider !== Provider.MANUAL;
    });

    if (providerStepData.length) {
      // If we got here, the convo has at least one AI-generated step
      const newestStepData = providerStepData.at(-1);
      const steps = Object.values(newestStepData.stepMap);

      steps.sort((a, b) => {
        if (!a.isAlternate) {
          return -1;
        }

        if (!b.isAlternate) {
          return 1;
        }

        return +(a.config.modelAlias > b.config.modelAlias);
      });

      // For each step in the group, set the respective model selection menu to
      // that step's model
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        let alias = step.config.modelAlias;

        if (!CurrentModels.includes(alias)) {
          alias = Alias.DEFAULT;
        }

        this.modelMenuOptionGroups[i].changeSelected(alias, true);
      }
    } else {
      // If we got here, the convo has no AI-generated steps yet
      for (let i = 0; i < this.modelMenuOptionGroups.length; i++) {
        let alias: Alias = Alias.NONE;

        if (i === 0) {
          if (this.topicConfig?.defaults.configSet?.primary?.modelAlias) {
            alias = this.topicConfig?.defaults.configSet?.primary?.modelAlias;
          } else {
            alias = Alias.DEFAULT;
          }
        }

        this.modelMenuOptionGroups[i].changeSelected(alias, true);
      }
    }

    this.updateModelOrderFromMenu();
  }


  protected updateModelOrderFromMenu() {
    this.modelOrder = this.modelMenuOptionGroups.map(og => og.getSelected().id);
  }


  private focusOnInput() {
    setTimeout(() => {
      this.$textInput?.nativeElement.focus();
    });
  }


  private resizeTextInputBuffer() {
    this.textInputBufferHeight = (this.$textInputArea.nativeElement as HTMLElement).clientHeight;
  }


  /**
   * Prepends "@moti " to the user's input
   *
   * @protected
   */
  protected addAtMotiToInput() {
    this.userEntry = `@moti ${this.userEntry}`;
    this.onInputChange();

    setTimeout(() => {
      this.$textInput.nativeElement.focus();
      const range = document.createRange();
      range.selectNodeContents(this.$textInput.nativeElement);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    });
  }


  protected onInputChange() {
    this.resizeTextInputBuffer();
    this.setDeferralProperties();
  }


  /**
   * Fetch and cache a contributor user
   *
   * @private
   * @param userId The user ID to fetch
   */
  private fetchContributorUser(userId: UserId): void {
    // Skip if we already have this user
    if (this.contributorUsers.has(userId)) {
      return;
    }

    // Check if it's the current session user first (avoid unnecessary API call)
    if (this.sess.user && String(userId) === String(this.sess.user.ID)) {
      this.contributorUsers.set(userId, this.sess.user);
      return;
    }

    // Fetch the user from the API
    this.usersService.fetchUser$(userId).subscribe({
      next: (user) => {
        this.contributorUsers.set(userId, user);
      }
    });
  }

  /**
   * Get a contributor user by ID
   *
   * @param userId The user ID
   * @returns The User object or undefined if not yet loaded
   */
  getContributorUser(userId: UserId): User | undefined {
    return this.contributorUsers.get(userId);
  }

  /**
   * Call this anytime either of the "defer response" properties of this class may
   * need to be recalculated.
   *
   * Generally, anytime this.contributorIds is updated
   *
   * @private
   * @see shouldDeferResponse
   * @see shouldShowDeferResponseState
   */
  private setDeferralProperties() {
    // Check if AI feature is enabled
    const isAiEnabled = this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MOTI_AI);

    if (!isAiEnabled) {
      // Feature flag is off - disable AI functionality
      this.shouldDeferResponse = true;
      this.shouldShowDeferResponseState = false;
      return;
    }

    // Feature flag is on - enable Moti AI functionality
    const nonSessUserContributorIds = [...this.contributorIds].filter(id => id !== this.sess.user.ID);

    if (nonSessUserContributorIds.length) {
      const regExp = new RegExp(/(?<!\w)@moti(?!.ai)/ig);
      this.shouldDeferResponse = !regExp.test(this.userEntry);
    } else {
      // In single-user convos, Moti always responds
      this.shouldDeferResponse = false;
    }

    this.shouldShowDeferResponseState = true;
  }


  /**
   *
   * @param stepId
   * @private
   */
  private scrollToStep(stepId: UUID) {
    const addlOffset = 220;
    const delay = 0;

    setTimeout(() => {
      const bubbleEl = document.getElementById(`step-${stepId}`);
      const bubbleElOffsetParent = bubbleEl?.offsetParent as HTMLElement;

      // if we are updating a step in a bubble, we want to start scrolling from the top of that step
      // (in case there's multiple steps in one bubble-group, and we need to focus on one of them.)
      // for other cases we just scroll to the bottom of that bubble-group.
      let top;
      if (this.convoSvc.scrollToBubbleGroup) {
        top = (bubbleElOffsetParent.offsetTop + bubbleElOffsetParent.offsetHeight) - addlOffset;
      } else {
        top = (bubbleElOffsetParent.offsetTop + bubbleEl.offsetHeight + bubbleEl.offsetTop) - addlOffset;
      }

      if (bubbleEl && bubbleElOffsetParent) {
        this.$convoBubblesArea.nativeElement.scrollTo({
          top,
          behavior: 'smooth',
        });
      }
    }, delay);
  }


  onPressEnter(event: Event) {
    const keyEvent = event as KeyboardEvent;

    if (keyEvent.shiftKey) {
      return;
    }

    event.preventDefault();

    this.submit();
  }


  submit() {
    if (!this.userEntry.trim()) {
      return;
    }

    if (this.waitingForResponse) {
      return;
    }

    // Update shouldDeferResponse based on current input to ensure
    // we detect @moti even if user hits Enter immediately after typing
    this.setDeferralProperties();

    this.sendStep();
  }


  sendStep() {
    if (this.countWords(this.userEntry) > MAX_WORDS_PER_STEP) {
      this.shade.errorDialog('Your message is too long. Up to 2,000 words are supported per message.');
      return;
    }

    this.waitingForResponse = true;

    const step: Step = {
      convoId: this.convo.id,
      text: this.userEntry,
      createdBy: this.sess.user.ID,
      shouldDeferResponse: this.shouldDeferResponse,
    };

    step.config = {
      provider: Provider.OVERRIDE ?? this.stepProvider[0],
      modelAlias: Alias.OVERRIDE ?? this.stepAlias[0],
    };

    const altStepConfigs: StepConfig[] = [];

    for (let i = 1; i < this.stepAlias.length; i++) {
      if (this.stepAlias[i] !== Alias.NONE) {
        altStepConfigs.push({
          provider: this.stepProvider[i],
          modelAlias: this.stepAlias[i],
        });
      }
    }

    if (altStepConfigs.length) {
      this.convoSvc.sendNewStepGroup({
        step: step,
        config: {
          primary: step.config,
          alternate: altStepConfigs,
        },
      });
    } else {
      this.convoSvc.sendNewStep(step);
    }

    this.userEntry = '';

    this.resizeTextInputBuffer();
    this.scrollToBottom('smooth');
  }


  private countWords(text: string) {
    text = text.trim();
    let words = text.split(/\s+/);
    words = words.filter(word => word.length > 0);
    return words.length;
  }


  viewOnlyInfoMessage: string = `As a viewer, you can watch this convo live, but cannot send messages. You can ask any admin or owner of this convo to change your role to one which allows you to send messages.`;


  /**
   * The next three variables are all set exclusively in the callbacks found inside of
   * generateModelMenuOptionGroup (further below in this class)
   */
  private stepProvider: Provider[] = [Provider.OpenAi, Provider.NONE, Provider.NONE];
  private stepAlias: Alias[] = [Alias.Gpt5, Alias.NONE, Alias.NONE];

  modelMenuOptionGroups: MotiOptionGroup<Alias>[] = [];
  modelMenuVisible: boolean = false;


  /**
   * Produces menu options for any of the model selection "slots" based on the supplied
   * index, which is utilized in the callback models inside the return MotiOptionGroup.
   *
   * This function is designed to be called once for every model selection "slot". A
   * "slot" can either be the primary model or any of the alternate models.
   *
   * If index is 0, output is slightly different because index=0 implies primary model
   * while index>0 implies alternate model.
   *
   * @param index - The index for the currently selected models
   * @private
   */
  private generateModelMenuOptionGroup(index: number): MotiOptionGroup<Alias> {
    const options: MotiOption<Alias>[] = [];

    for (let id of CurrentModels) {
      if (id === Alias.NONE) {
        continue;
      }

      options.push({
        id: id,
        name: getAliasName(id),
        callback: () => {
          this.stepAlias[index] = id;
          this.stepProvider[index] = getAliasProvider(id);
        },
      });
    }

    for (let o of options) {
      if (isDeepModel(o.id)) {
        o.icon = {
          type: IconType.FontAwesome,
          id: 'sparkles',
        };
      }
    }

    if (index > 0) {
      options.splice(0, 0, {
        id: Alias.NONE,
        name: 'Disabled',
        callback: () => {
          this.stepAlias[index] = Alias.NONE;
          this.stepProvider[index] = Provider.NONE;
        },
        selected: true,
      });
    } else {
      options[0].selected = true;
    }

    return new MotiOptionGroup<Alias>(options);
  }


  protected getAtMotiInfoMessage(): string {
    const twoToNine = [
      '',
      '',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
    ];

    const count = this.contributorIds.length;
    let numStr: string = '';

    if (count > 9) {
      numStr = `${count}`;
    } else if (count > 1) {
      numStr = `${twoToNine[count]} users have`;
    } else {
      numStr = `one user has`;
    }


    return `In addition to Moti, ${numStr} contributed to this convo.<br><br>So when you send your message, to tell Moti you'd like Moti to respond, include the tag <u>@moti</u> anywhere in your message.<br><br>For example: <em>What is your opinion, @moti?</em>`;
  }


  protected contributorTextFactory = (user: User): string => {
    return `${user.Name} has contributed to this convo`;
  };


  protected checkIconHtml: string = `<i class="fa-solid fa-circle-check"></i>`;
  protected readonly MotiUrlService = MotiUrlService;


  protected onScrollToBottomClick() {
    this.showScrollToBottomButton = false;
    this.scrollToBottom('smooth');
  }
}
